<script>
import prettyBytes from 'pretty-bytes';

/* COMPOSANTS */
import AkLabel from '@components/v2/general/AkLabel';
import FileUpload from 'primevue/fileupload';

/* MIXINS */
import fileUploadMixin from '@mixins/fileUploadMixin';

export default {
  components: {AkLabel, FileUpload},
  mixins: [fileUploadMixin],
  inject: {pSubmitted: {default: null}, pDisabled: {default: null}},
  props: {
    label: {
      type: String,
      required: false,
    },
    modelValue: {
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    accept: {
      type: String,
      required: false,
      default: '',
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      id: null,
      showErrorMessage: false,
      messages: null,
    };
  },
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    hasCurrentValue() {
      return this.currentValue !== undefined && this.currentValue !== null;
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },
    isRequiredFailed() {
      return (
        this.isRequired &&
        this.ignoredValidator.indexOf('required') === -1 &&
        this.validator.required.$invalid &&
        this.alreadySubmitted
      );
    },
  },
  methods: {
    fileSize(f) {
      return prettyBytes(f.size, {locale: 'en', maximumFractionDigits: 1});
    },
    selectFile(event) {
      this.currentValue = event.files;
      this.showErrorMessage = false;
      this.messages = null;
      if (this.$refs.uploader.messages && this.$refs.uploader.messages.length) {
        this.showErrorMessage = true;
        this.messages = this.$refs.uploader.messages.map((message) => message).join(' ');
        this.$refs.uploader.onMessageClose();
      }
    },
    deleteFile(idx) {
      this.showErrorMessage = false;
      this.messages = null;
      this.$refs.uploader.onMessageClose();
      this.currentValue.splice(idx, 1);
    },
    clear() {
      this.showErrorMessage = false;
      this.messages = null;
      this.$refs.uploader.onMessageClose();
      this.$refs.uploader.clear();
    },
    uploadFile() {
      this.showErrorMessage = false;
      this.messages = null;
      this.$refs.uploader.onMessageClose();
      this.$refs.uploader.$refs.fileInput.click();
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control" :class="[className]">
    <AkLabel :required="this.isRequired" v-if="showLabel">
      {{ label }}
    </AkLabel>
    <FileUpload
      ref="uploader"
      :invalidFileSizeMessage="this.getInvalidFileSizeMessage()"
      :invalidFileTypeMessage="this.getInvalidFileTypeMessage(accept)"
      :maxFileSize="getMaxFileUpload()"
      :multiple="multiple"
      :accept="accept"
      :disabled="isDisabled"
      @select="selectFile">
      <template #header>
        <div></div>
      </template>
      <template #content="{files}">
        <div class="flex flex-col gap-2" v-for="(file, index) of files" :key="file.name + file.type + file.size">
          <div class="flex flex-row gap-2 items-center p-2 rounded-md">
            <div>
              <i class="ga-icon ga-document text-grey-400 text-3xl" />
            </div>
            <div class="truncate flex flex-col flex-1 hover:cursor-pointer gap-1">
              <span class="pointer text-grey-900 font-medium truncate text-base">{{ file.name }}</span>
              <span class="text-grey-400 font-normal text-sm">{{ fileSize(file) }}</span>
            </div>
            <span class="hover:cursor-pointer" @click="deleteFile(index)"
              ><i class="ga-icon ga-trash text-red text-2xl"
            /></span>
          </div>
        </div>
        <div
          v-if="files.length > 0"
          class="flex flex-col justify-center items-center gap-1 p-10 border-dashed rounded-md text-center border border-grey-200 hover:cursor-pointer md:hover:bg-secondary-50 md:hover:border-secondary"
          @click="uploadFile">
          <i class="ga-icon ga-upload text-3xl text-secondary" />
          <p class="text-base font-normal text-grey-900 md:hidden flex">Importer depuis votre galerie</p>
          <p class="text-base font-normal text-grey-900 hidden md:flex">Faites glisser un fichier ici</p>
          <p class="text-base font-normal text-grey-900 hidden md:block">Ou importer depuis votre ordinateur</p>
        </div>
      </template>
      <template #empty>
        <div
          :class="[{'fileempty-invalid': isRequiredFailed && alreadySubmitted}]"
          class="flex flex-col h-full justify-center items-center gap-1 p-10 border-dashed rounded-md text-center border border-grey-200 hover:cursor-pointer md:hover:bg-secondary-50 md:hover:border-secondary"
          @click="uploadFile">
          <i class="ga-icon ga-upload text-3xl text-secondary" />
          <p class="text-base font-normal text-grey-900 md:hidden flex">Importer depuis votre galerie</p>
          <p class="text-base font-normal text-grey-900 hidden md:flex">Faites glisser un fichier ici</p>
          <p class="text-base font-normal text-grey-900 hidden md:block">Ou importer depuis votre ordinateur</p>
        </div>
      </template>
    </FileUpload>
    <div class="flex flex-col flex-1">
      <small v-if="isRequiredFailed" class="p-error">
        {{ $t('field_required', [this.label]) }}
      </small>
      <small v-if="this.showErrorMessage" class="p-error">
        {{ this.messages }}
      </small>
    </div>
  </div>
</template>
